import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import AIModelSelector, { AI_MODELS, simulateModelOutput } from '@/components/AIModelSelector';
import CompareResults from '@/components/CompareResults';
import { Wand2, Copy, Check, Loader2, Sparkles, Columns } from 'lucide-react';
import { isAdminTester, logAdminBypass } from '@/utils/adminBypass';
import { getToastMessage } from '@/utils/errorClassifier';

const PromptEnhancer = () => {
  const { api, user, updateCredits } = useAuth();
  const [prompt, setPrompt] = useState('');
  const [style, setStyle] = useState('general');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState({});
  const [showCompare, setShowCompare] = useState(false);
  
  // AI Model selection
  const [selectedModel, setSelectedModel] = useState('gpt');

  const styles = [
    { value: 'general', label: 'General Purpose' },
    { value: 'midjourney', label: 'Midjourney Optimized' },
    { value: 'product', label: 'Product Photography' },
    { value: 'artistic', label: 'Artistic/Creative' },
  ];

  const handleEnhance = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }

    // Check admin bypass using shared utility
    const isAdmin = isAdminTester(user);
    logAdminBypass('PromptEnhancer', 'handleEnhance', user, isAdmin);
    
    if (!isAdmin && (user?.credits || 0) < 2) {
      toast.error('Insufficient credits. Please upgrade your plan.');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/ai/enhance-prompt', {
        prompt: prompt.trim(),
        style,
        ai_model: selectedModel
      });
      
      const baseResult = response.data.result;
      setResult(baseResult);
      
      // Only update credits if not admin
      if (!isAdmin && response.data.credits_used > 0) {
        updateCredits((user?.credits || 0) - response.data.credits_used);
      }
      toast.success(`Prompt enhanced with ${AI_MODELS.find(m => m.id === selectedModel)?.name}!${isAdmin ? ' (Admin mode)' : ` ${response.data.credits_used} credits used`}`);
    } catch (error) {
      toast.error(getToastMessage(error, 'Failed to enhance prompt. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  // Handle model change - instantly update results
  const handleModelChange = (newModel) => {
    setSelectedModel(newModel);
    
    if (result) {
      const simulatedResult = {
        ...result,
        enhanced: simulateModelOutput(newModel, result.enhanced || ''),
        variations: (result.variations || []).map(v => simulateModelOutput(newModel, v)),
      };
      setResult(simulatedResult);
    }
  };

  const copyToClipboard = async (text, key) => {
    await navigator.clipboard.writeText(text);
    setCopied({ ...copied, [key]: true });
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied({ ...copied, [key]: false }), 2000);
  };

  const applyVariation = (variation) => {
    setPrompt(variation);
    setResult(null);
    setComparisonResults(null);
    toast.success('Variation loaded as input');
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 fade-in" data-testid="prompt-enhancer-page">
      {/* Left Panel - Controls */}
      <div className="lg:col-span-4 space-y-6">
        <div>
          <h2 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Enhance Prompt
          </h2>
          <p className="text-sm text-muted-foreground">
            Transform your prompt for better AI outputs (2 credits)
          </p>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="prompt">Your Prompt</Label>
            <Textarea
              id="prompt"
              placeholder="Enter your prompt here..."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              className="min-h-[120px] resize-none"
              data-testid="prompt-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="style">Enhancement Style</Label>
            <Select value={style} onValueChange={setStyle}>
              <SelectTrigger data-testid="style-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {styles.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* AI Model Selector */}
        <div className="bg-muted/30 rounded-xl p-4">
          <AIModelSelector
            selectedModel={selectedModel}
            onModelChange={handleModelChange}
            variant="pills"
            compact={true}
          />
        </div>

        <Button 
          className="w-full" 
          onClick={handleEnhance}
          disabled={!prompt.trim() || loading}
          data-testid="enhance-btn"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Enhancing...
            </>
          ) : (
            <>
              <Wand2 className="w-4 h-4 mr-2" />
              Enhance Prompt
            </>
          )}
        </Button>

        {/* Compare Results Toggle */}
        <Button
          variant="outline"
          className="w-full"
          onClick={() => setShowCompare(!showCompare)}
          data-testid="toggle-compare-btn"
        >
          <Columns className="w-4 h-4 mr-2" />
          {showCompare ? 'Hide Compare' : 'Compare Results'}
        </Button>
      </div>

      {/* Right Panel - Results */}
      <div className="lg:col-span-8 space-y-6">
        {/* Compare Results Panel */}
        {showCompare && (
          <CompareResults 
            prompt={prompt}
            style={style}
            selectedModel={selectedModel}
          />
        )}

        <Card className="h-full">
          <CardContent className="p-6">
            {!result ? (
              <div className="preview-canvas h-full min-h-[400px]">
                <div className="text-center">
                  <div className="w-16 h-16 bg-muted rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Wand2 className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <p className="text-muted-foreground">
                    Enter a prompt to see enhanced versions
                  </p>
                </div>
              </div>
            ) : (
              // Single Model View
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                {/* Enhanced Prompt */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-primary" />
                      Enhanced Prompt
                      <span className="text-xs text-muted-foreground ml-2">
                        ({AI_MODELS.find(m => m.id === selectedModel)?.icon} {AI_MODELS.find(m => m.id === selectedModel)?.name})
                      </span>
                    </Label>
                  </div>
                  <div className="bg-muted/30 rounded-xl p-4 border border-border" data-testid="enhanced-result">
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{result.enhanced}</p>
                  </div>
                  {/* Copy Button - Prominent */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => copyToClipboard(result.enhanced, 'enhanced')}
                    data-testid="copy-enhanced"
                  >
                    {copied.enhanced ? (
                      <>
                        <Check className="w-4 h-4 mr-2 text-green-500" />
                        Copied to Clipboard
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-2" />
                        Copy Prompt
                      </>
                    )}
                  </Button>
                </div>

                {/* Variations */}
                {result.variations && result.variations.length > 0 && (
                  <div className="space-y-3">
                    <Label>Variations</Label>
                    {result.variations.map((variation, index) => (
                      <div 
                        key={index}
                        className="bg-muted/20 rounded-xl p-4 border border-border space-y-2"
                        data-testid={`variation-${index}`}
                      >
                        <p className="text-sm leading-relaxed whitespace-pre-wrap">{variation}</p>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => copyToClipboard(variation, `var-${index}`)}
                          >
                            {copied[`var-${index}`] ? (
                              <>
                                <Check className="w-3 h-3 mr-1.5 text-green-500" />
                                Copied
                              </>
                            ) : (
                              <>
                                <Copy className="w-3 h-3 mr-1.5" />
                                Copy
                              </>
                            )}
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => applyVariation(variation)}
                            data-testid={`use-variation-${index}`}
                          >
                            Use This
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Tips */}
                {result.tips && (
                  <div className="bg-accent/50 rounded-xl p-4">
                    <Label className="mb-2 block">Tips</Label>
                    <p className="text-sm text-muted-foreground">{result.tips}</p>
                  </div>
                )}
              </motion.div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PromptEnhancer;
